import type { ReviewItem, ReviewsSummary } from './types';

/** Mirrors `MODAL_LIMIT` in the backend's `GoogleReviewsService`. */
const JSON_LD_REVIEW_LIMIT = 20;

/**
 * The review list for structured data: the plain top 20 qualifying reviews by
 * recency.
 *
 * The resolver deduplicates `modalItems` against `sliderItems` so a visitor
 * does not read the same review twice, which is a display concern and has no
 * business reaching a crawler — it would drop up to five reviews from the
 * structured data. Merging the two lists back together restores the plain
 * list: `modalItems` is the top 20 minus the slider's reviews, so the union
 * contains every one of the 20 most recent qualifying reviews. Both queries
 * draw from the same population (rating >= 4, non-empty body).
 *
 * Reviews carry no id in the payload, so two entries are the same review when
 * timestamp, rating and body all match. Entries that alike are indistinguishable
 * in the output anyway.
 */
/** A review without a parseable timestamp cannot be ordered, and Schema.org
 * wants `datePublished` anyway, so it is left out rather than sorted to an
 * arbitrary position. */
const publishedAt = (item: ReviewItem) => new Date(item.publishTime).getTime();

export function mergeJsonLdReviewItems(
  sliderItems: ReviewItem[],
  modalItems: ReviewItem[],
): ReviewItem[] {
  const seen = new Set<string>();
  const merged: ReviewItem[] = [];
  for (const item of [...sliderItems, ...modalItems]) {
    const key = `${item.publishTime}|${item.rating}|${item.reviewBody}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    merged.push(item);
  }
  return merged
    .filter((item) => !Number.isNaN(publishedAt(item)))
    .sort((a, b) => publishedAt(b) - publishedAt(a))
    .slice(0, JSON_LD_REVIEW_LIMIT);
}

/**
 * The schema.org `AggregateRating` for a review summary, or null when there is
 * no rating to report — an average without a positive count. Shared by this
 * widget's Organization block and the Standort's `LocalBusiness` node built by
 * School Details, so both publish the same shape.
 */
export function buildAggregateRatingJsonLd(
  summary: ReviewsSummary,
): Record<string, unknown> | null {
  if (
    summary.googleAverageRating == null ||
    summary.googleTotalReviewCount <= 0
  ) {
    return null;
  }
  return {
    '@type': 'AggregateRating',
    'ratingValue': summary.googleAverageRating,
    'reviewCount': summary.googleTotalReviewCount,
    'bestRating': 5,
    'worstRating': 1,
  };
}

/** The schema.org `Review` entries for a review list; unrated items are left out. */
export function buildReviewListJsonLd(
  items: ReviewItem[],
): Record<string, unknown>[] {
  return items
    .filter(
      (item): item is ReviewItem & { rating: number } => item.rating != null,
    )
    .map((item) => ({
      '@type': 'Review',
      'datePublished': item.publishTime,
      ...(item.reviewBody ? { reviewBody: item.reviewBody } : {}),
      ...(item.authorName
        ? { author: { '@type': 'Person', 'name': item.authorName } }
        : {}),
      'reviewRating': {
        '@type': 'Rating',
        'ratingValue': item.rating,
      },
    }));
}

/**
 * Whether the `Organization` node away from a Standort page publishes the
 * Google `aggregateRating` (SH-543).
 *
 * On, and expected to stay on: the brand's eKomi widget has no `is_rich` and
 * must never get it (SH-541, spec 60.12), so eKomi publishes no rating on
 * these pages and there is no duplicate to avoid — unlike a Standort page,
 * where `PUBLISH_GOOGLE_AGGREGATE_RATING` is off for exactly that reason. The
 * switch exists so the two nodes can be reasoned about as a pair; turning it
 * off leaves the node with its identity alone, because the `review` list is
 * off as well (`PUBLISH_GOOGLE_ORGANIZATION_REVIEW_LIST`).
 */
export const PUBLISH_GOOGLE_ORGANIZATION_AGGREGATE_RATING: boolean = true;

/**
 * Whether the `Organization` node away from a Standort page publishes its
 * `review` list.
 *
 * Off, together with the `LocalBusiness` node's `PUBLISH_GOOGLE_REVIEW_LIST`.
 * Google's review-snippet rules want `aggregateRating` on the same item that
 * carries the reviews, and it does not show review snippets a business
 * publishes about itself at all, so the list earns nothing here either. This
 * node is not what Search Console flagged — it has its rating — but the two
 * are switched off as a pair so the site publishes no self-serving `review`
 * anywhere. Kept as a switch, not deleted: the builders stay ready for the
 * rating test D6 anticipates.
 */
export const PUBLISH_GOOGLE_ORGANIZATION_REVIEW_LIST: boolean = false;

/** The brand the rating is attributed to away from a Standort page. */
export interface ReviewsJsonLdOrganization {
  name: string;
  url: string;
}

/**
 * Builds the AggregateRating (+ review[]) JSON-LD block the Reviews widget
 * emits **away from a Standort page**, as an inline Organization node: there
 * is no single physical business to attach the rating to. On a Standort page
 * the widget emits nothing — the rating belongs to the `LocalBusiness` node
 * School Details builds (SH-468, spec 10.03.02.01.20).
 *
 * Returns null when there's no rating to report.
 */
export function buildReviewsJsonLd(
  summary: ReviewsSummary,
  items: ReviewItem[],
  organization: ReviewsJsonLdOrganization,
): Record<string, unknown> | null {
  const aggregateRating = buildAggregateRatingJsonLd(summary);
  if (!aggregateRating) {
    return null;
  }
  const review = buildReviewListJsonLd(items);

  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    // Identified, not anonymous: an editor may place the widget twice on a
    // page, and two anonymous nodes would describe the same brand twice.
    '@id': `${organization.url}#organization`,
    'name': organization.name,
    'url': organization.url,
    ...(PUBLISH_GOOGLE_ORGANIZATION_AGGREGATE_RATING
      ? { aggregateRating }
      : {}),
    ...(PUBLISH_GOOGLE_ORGANIZATION_REVIEW_LIST && review.length
      ? { review }
      : {}),
  };
}
